import { NgModule, LOCALE_ID } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FlexLayoutModule } from '@angular/flex-layout';
import { MatNativeDateModule } from '@angular/material/core';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { ToastrModule } from 'ngx-toastr';
import { LoadingBarHttpClientModule } from '@ngx-loading-bar/http-client';
import { AkitaNgDevtools } from '@datorama/akita-ngdevtools';
import { AppRoutingModule } from './app-routing.module';
import { SharedModule } from './modules/shared/shared.module';
import { LayoutModule } from './modules/layout/layout.module';
import { UserModule } from './modules/user/user.module';
import { ReferencesModule } from './modules/reference/reference.module';
import { UserRegistrationRoutingModule } from './modules/user-registration/user-registration-routing.module';

import {
  DefaultTranspiler,
  provideTransloco,
  TRANSLOCO_CONFIG,
  TRANSLOCO_TRANSPILER,
  TranslocoConfig,
  TranslocoService
} from '@ngneat/transloco';
import { HttpLoader } from './services/transloco.loader';

import { TokenInterceptor } from './modules/authentication/services/token.interceptor';
import { ErrorNotifierInterceptor } from './modules/user-registration/interceptors/error-notifier.interceptors';
import { PaginatorI18n } from './modules/shared/services/mat-paginator-intl';
import { Languages } from './modules/shared/constants/shared.constants';
import { environment } from '../environments/environment';

import localeAr from '@angular/common/locales/ar';
import localeArExtra from '@angular/common/locales/extra/ar';
import { registerLocaleData } from '@angular/common';

import { AppComponent } from './app.component';

registerLocaleData(localeAr, 'ar', localeArExtra);

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    HttpClientModule,
    AppRoutingModule,
    SharedModule,
    LayoutModule,
    UserModule,
    ReferencesModule,
    UserRegistrationRoutingModule,
    MatNativeDateModule,
    FlexLayoutModule,
    FormsModule,
    ReactiveFormsModule,
    LoadingBarHttpClientModule,
    ToastrModule.forRoot({
      timeOut: 5000,
      positionClass: 'toast-bottom-right',
      preventDuplicates: true,
      closeButton: true,
      progressBar: true,
    }),
    environment.production ? [] : AkitaNgDevtools.forRoot()
  ],
  providers: [
    {
      provide: TRANSLOCO_CONFIG,
      useValue: {
        availableLangs: (Languages ?? []).map((lang) => lang.code),
        defaultLang: Languages?.find((lang) => lang.isDefault)?.code || 'en',
        prodMode: environment.production,
        reRenderOnLangChange: true,
      } as TranslocoConfig,
    },
    {
      provide: LOCALE_ID,
      useFactory: () =>
        Languages.find((lang) => lang.isDefault)?.locale || 'en-US',
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: TokenInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorNotifierInterceptor,
      multi: true,
    },
    {
      provide: 'api_url',
      useValue: environment.apiUrl,
    },
    {
      provide: MatPaginatorIntl,
      deps: [TranslocoService],
      useFactory: (translateService: TranslocoService) =>
        new PaginatorI18n(translateService).getPaginatorIntl(),
    },
    {
      provide: TRANSLOCO_TRANSPILER,
      useClass: DefaultTranspiler,
    },
    provideTransloco({
      config: {
        availableLangs: Languages.map((lang) => lang.code),
        defaultLang: Languages.find((lang) => lang.isDefault)?.code || 'en',
        reRenderOnLangChange: true,
        prodMode: environment.production,
      },
      loader: HttpLoader,
    }),
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
